import { useCallback, useState } from 'react';

export interface FileProgressEntry {
  key: string;
  filename: string;
  progress: number;
  type: 'upload' | 'download';
  isCompleted?: boolean;
}

export const useFileProgress = () => {
  const [progressEntries, setProgressEntries] = useState<Map<string, FileProgressEntry>>(new Map());

  const startFileOperation = useCallback((key: string, filename: string, type: 'upload' | 'download') => {
    setProgressEntries((prev) => {
      const next = new Map(prev);
      next.set(key, { key, filename, progress: 0, type, isCompleted: false });
      return next;
    });
  }, []);

  const updateFileProgress = useCallback((key: string, progress: number) => {
    setProgressEntries((prev) => {
      const entry = prev.get(key);
      if (!entry) return prev;

      const next = new Map(prev);
      next.set(key, { ...entry, progress });
      return next;
    });
  }, []);

  const completeFileOperation = useCallback((key: string) => {
    // Mark as completed first
    setProgressEntries((prev) => {
      const entry = prev.get(key);
      if (!entry) return prev;

      const next = new Map(prev);
      next.set(key, { ...entry, progress: 100, isCompleted: true });
      return next;
    });

    // Remove after 1 second
    setTimeout(() => {
      setProgressEntries((prev) => {
        const next = new Map(prev);
        next.delete(key);
        return next;
      });
    }, 1000);
  }, []);

  const getProgressForFile = useCallback(
    (fileKey: string): FileProgressEntry | undefined => {
      return progressEntries.get(fileKey);
    },
    [progressEntries]
  );

  const getProgressLabel = useCallback(
    (progress: number, type: 'upload' | 'download', isCompleted?: boolean): string => {
      if (isCompleted) {
        const action = type === 'upload' ? 'Uploaded' : 'Downloaded';
        return `(${action}!)`;
      }
      const action = type === 'upload' ? 'Uploading' : 'Downloading';
      return `(${action}... ${progress}%)`;
    },
    []
  );

  return {
    progressEntries,
    startFileOperation,
    updateFileProgress,
    completeFileOperation,
    getProgressForFile,
    getProgressLabel,
  };
};
